/* ---------------------------------------------------------------------------
   The guide: what this application does, and how to do each of the things it
   is for.

   WHY IT IS A SCREEN AND NOT A README. The README is written for whoever is
   going to change this code; it explains why the map has three modes and how the
   reconciler patches a tree. None of that is what a Divisional Engineer being
   shown the register for the first time needs, and neither is it something they
   can reach — it is a file in a repository they have no account on. This screen
   is the same subject written for the person using the thing, inside the thing,
   available offline with the rest of it.

   ONE PAGE, NOT A LITTLE SITE. Every section is on screen at once and the
   contents at the top scroll to them, because the two ways somebody arrives here
   are opposite: "show me what this does" reads top to bottom, and "how do I log a
   defect" wants one heading. A section-per-route version would need a
   `guideSection` in state, an encoding in the address, and a back button that
   means something different here than everywhere else — three moving parts to
   replace a scroll.

   IT IS DELIBERATELY REACHABLE IN BOTH SHELLS. The field app carries four
   screens and this is the fifth, reached from Options rather than from a tab —
   the same way the record is reached from the Bridges list. An inspector on a
   district phone is the reader with the least chance of being shown the
   application by somebody who already knows it, so "documentation is desk work"
   would be exactly the wrong line to draw. See FIELD_SCREENS in src/core/shell.ts.

   WHAT IS WRITTEN HERE HAS TO BE TRUE. This screen describes controls by the
   words on them and states what the application will and will not do. A guide
   that has drifted from the code is worse than no guide, because it is believed:
   somebody follows a step that is not there any more and concludes the
   application is broken. `npm test` checks the claims it can — that every screen
   this page numbers is one the application has, in the order the navigation pane
   draws them, and that no task is attributed to a shell that cannot perform it.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, card, cardHead, fact, tag } from '../components/widgets.ts';
import { panel, panelButton } from '../components/panel.ts';
import { navFor, SCREEN_NAV } from '../components/navpane.ts';
import { APPS, appById, type AppDef } from '../core/apps.ts';
import { openApp } from '../core/navigate.ts';

import { FIELD_MAX_WIDTH, FIELD_TABS } from '../core/shell.ts';
import { ROLES, ROLE_COPY } from '../data/roles.ts';
import { agency } from '../tenant/agency.ts';
import { active } from '../tenant/registry.ts';

/* --------------------------- the content --------------------------- */

/**
 * Which interface a task belongs to.
 *
 * Not decoration. A good half of what this guide exists to answer is somebody
 * looking for a control on the device that does not have it — the inventory
 * sheet on a phone, the rate table in the field — and a heading that did not say
 * which app it was describing would generate exactly those questions rather than
 * answer them.
 */
type Where = 'both' | 'web' | 'field';

const WHERE_LABEL: Record<Where, string> = {
  both: 'Both apps',
  web: 'Web app only',
  field: 'Field app',
};

/** Rank on the shared monochrome ramp — see tag() in widgets.ts. */
const WHERE_RANK: Record<Where, 1 | 2 | 3> = { both: 2, web: 1, field: 3 };

interface Task {
  title: string;
  where: Where;
  /** Numbered, and each one is a thing to do rather than a thing to know. */
  steps: string[];
  /** What the steps cannot say: a limit, a refusal, or what happens afterwards. */
  note?: string;
}

interface Section {
  /** The DOM id the contents list scrolls to. Prefixed so nothing else collides. */
  id: string;
  title: string;
  /** One sentence saying what the section is for, under the heading. */
  lead: string;
  /** Prose, where the thing being explained is not a sequence of steps. */
  paras?: string[];
  /** Term-and-explanation pairs — screens, tabs, controls, rules. */
  facts?: { k: string; v: string }[];
  tasks?: Task[];
}

/**
 * The applications this build draws, for the sections that describe them.
 *
 * Home is left out: it is the page in front of the applications rather than one
 * of them, and a guide that listed it as a fifth product would be describing a
 * different site from the one the rail offers. Bridge Rating is left out here
 * and named by hand in the section below, because it is a separate build whose
 * screens this one cannot read.
 */
const DESK_APPS: readonly AppDef[] = APPS.filter((a) => a.id !== 'home' && a.id !== 'structure' && !a.separate);

const SECTIONS: Section[] = [
  {
    id: 'guide-start',
    title: 'Start here',
    lead: 'One register, two interfaces, and one identity behind both.',
    paras: [
      'Bridge Watch is the Department of Roads’ bridge inventory and condition register. ' +
        'Everything recorded in it — an inspection, an issue, a photograph, a position, a 3D ' +
        'model — is written to one register on the Department’s servers. Which interface you ' +
        'are using changes what is on screen; it never changes what is stored, and two people ' +
        'on two devices are always reading the same records.',
      'The application chooses its interface from the device it opens on. A touchscreen ' +
        `${FIELD_MAX_WIDTH} pixels wide or narrower gets the field app; everything else gets ` +
        'the web app. That is a starting point rather than a verdict — the switch is on the ' +
        'Options screen of both, and adding ?shell=field or ?shell=desk to the address forces ' +
        'either one onto any device.',
      'You sign in with a username and a password the register itself keeps, and what you ' +
        'record is attributed to that username. A session lasts twelve hours — when yours ends, ' +
        'every screen is replaced by the sign-in page, and anything you had recorded and not yet ' +
        'sent is still on the device and goes up when you return. Signing out from Options ends ' +
        'the session deliberately, on the register rather than only on this device.',
      'What you may DO once you are in is a separate question, and the answer is your role. ' +
        'There are five, they are explained below, and the one you hold decides which controls ' +
        'you are given: an inspector records inspections and issues, an engineer also maintains ' +
        'the inventory and the rates, an administrator also grants the roles. It is printed in the ' +
        'account menu under your name, top right of the web app, and on the Options screen of both.',
    ],
    facts: [
      {
        k: 'The web app',
        v:
          `Four applications over one register, each its own page with a navigation pane down ` +
          `the left: ${DESK_APPS.map((a) => a.label).join(', ')}. For a desk: the whole ` +
          'register, the printed documents, importing, and administration.',
      },
      {
        k: 'The field app',
        v: `${FIELD_TABS.length} tabs and the bridge record. For a phone or tablet at a structure: find it, get to it, record what you found — with or without a signal.`,
      },
      {
        k: 'Switching between them',
        v: 'Options → This device → Interface. Or open the address with ?shell=field or ?shell=desk.',
      },
    ],
  },

  {
    id: 'guide-apps',
    title: 'The four applications',
    lead:
      'One register, one sign-in, four applications over it. Each is its own page, and the ' +
      'list at the foot of the navigation pane moves between them carrying the structure you ' +
      'have in hand.',
    facts: DESK_APPS.map((app) => ({
      k: app.label,
      /* The screens are read off the app's own list, so one added to it cannot
         leave this line naming four when the pane draws five. The blurb is the
         same sentence the home page's card prints, for the same reason. */
      v: `${app.blurb} Screens: ${navFor(app).map((e) => e.label).join(', ')}.`,
    })).concat([
      {
        k: 'Bridge Rating',
        v:
          `${appById('rating').blurb} It is a separate application with its own workspace — ` +
          'four screens: Structures, Rate a structure, Ratings, Method - and it opens in its ' +
          'own page from the applications list in the navigation pane.',
      },
      {
        k: 'Moving between them',
        v:
          'The list at the foot of the navigation pane, or the four buttons on every row of ' +
          'the home page table, or the strip at the top of a programme or a site record. All ' +
          'three carry the structure across, so leaving a record for its programme opens that ' +
          'bridge’s programme rather than whichever one you last looked at.',
      },
    ]),
  },

  {
    id: 'guide-web',
    title: 'The web app, screen by screen',
    lead:
      'Every screen the four applications draw. Which of them you see at once depends on the ' +
      'application you are in; the navigation pane of each names its own.',
    facts: [
      {
        k: SCREEN_NAV.dashboard.label,
        v: 'The home page. The four applications across the top, then the headline figures, then every structure on file in one table — with its inspection standing, its open issues, and four buttons that open it in whichever application the job belongs to. Underneath: freeboard margins, superstructure mix and years of completion, inspection standing, open issues by severity, and flood, scour and seismic screening.',
      },
      {
        k: SCREEN_NAV.board.label,
        v: 'One structure, every application’s answer about it at once: where it is in its life and what falls due next, the programme and its forecast slip, what is open on site, the handover and the model of record, the inspection standing and condition, open defects by severity, the governing rating and posting, and the verdict — open, posted or closed — with who decided and when. A tile with no answer for the structure’s state says why in a word, such as Not in service or No baseline. Opened from the Dashboard button on a register row, the foot of every application’s explorer, the structure’s id in the status bar, or #/bridge/<id>/dashboard.',
      },
      {
        k: SCREEN_NAV.list.label,
        v: 'Every structure on file. Carried by three applications, and a row means a different thing in each: in Bridge Inspection the structure name opens the record, in Construction Scheduler the programme, in Construction Monitoring the site record. The header search filters as you type, the district chips narrow it, the column headings sort it, and Export CSV downloads exactly what is on screen. Import brings inventory rows in from a spreadsheet.',
      },
      {
        k: SCREEN_NAV.network.label,
        v: 'The map, in three views. Flat is the tile map: every structure pinned, deck footprints from zoom 14, and the only view that works with no connection. Site puts one structure on its own terrain to orbit. Fly tips the whole network over so you can follow a road across it. Street and Topo switch the base map. Both 3D views need a recorded position and say so when there is none.',
      },
      {
        k: SCREEN_NAV.detail.label,
        v: 'One structure, in Bridge Inspection: its 3D model, the inventory transcribed from the drawings, the inspection history, the issue log, and the recommended remedial work with what it would cost. Log inspection, Log issue and Edit record sit in the Inspection ribbon; Import 3D model is on the structure’s dashboard on the start page. What was built on the structure and the programme it was built against are not here — they are Construction Monitoring’s and Construction Scheduler’s, and their tabs in the ribbon open either one on this structure. On a phone the record does carry the Works card, because a pour is recorded at the pour.',
      },
      {
        k: SCREEN_NAV.report.label,
        v: 'The printable inventory sheet for the selected structure. Print sends it to paper or to PDF.',
      },
      {
        k: SCREEN_NAV.sheet.label,
        v: 'The Bridge Inspection Summary for the selected structure, on the form the guideline sets out: identification, the latest inspection, the ten items with condition, extent and urgency, and the recommended remedial work. Opened from Inspection summary sheet in the Inspection ribbon; Print, PDF and Word are on its panel.',
      },
      {
        k: SCREEN_NAV.summary.label,
        v: 'The printable network report, grouped by district, by division road office, or by road classification.',
      },
      {
        k: SCREEN_NAV.schedule.label,
        v: 'Construction Scheduler’s screen for one structure: the baseline frozen from its 3D model, which scheduled activities have been matched to which components, what has been declared complete and what has been started, and the variance between the plan and what the site has recorded. It gives two completion figures and never one — the truth is between them.',
      },
      {
        k: SCREEN_NAV.site.label,
        v: 'Construction Monitoring’s screen for one structure: everything recorded on site against it — work with its quantity and unit, daily returns of weather, labour and plant, incidents, and filed documents. It is also where an entry recorded on a phone is verified.',
      },
      {
        k: SCREEN_NAV.works.label,
        v: 'The construction report: what was recorded across every structure over a week, a month, a quarter or a range you choose, grouped by structure and laid out to the headings a monthly progress report is read against. Empty for a register of bridges already in service, which is most of them.',
      },
      { k: SCREEN_NAV.guide.label, v: 'This page. It is in every application’s pane.' },
      {
        k: SCREEN_NAV.people.label,
        v: 'Who may use this register, and as what. Add somebody, give them a role, issue them a password, and read the record of every such change underneath — with who made it. One role per person across all four applications. Administrators only; for everybody else this destination is not in the pane at all.',
      },
    ],
  },

  {
    id: 'guide-field',
    title: 'The field app, tab by tab',
    lead: `${FIELD_TABS.length} tabs, because an inspector at a structure is doing one of three things.`,
    facts: [
      {
        k: 'Bridges',
        v: 'The register, searchable. Tap a structure to open its record — on this app, unlike the web one, tapping a row does open it.',
      },
      {
        k: 'Map',
        v: 'Where the structures are, for getting to one. It needs a connection to fetch tiles: with no signal the tab is dimmed and says so rather than disappearing.',
      },
      {
        k: 'Options',
        v: 'Who this device is signed in as, what it is holding that the register has not got yet, the button that puts the app on the home screen, the interface, language and theme switches, and the way into this guide and into resetting offline storage.',
      },
      {
        k: 'The bridge record',
        v: 'Pushed from Bridges or from a map pin — it is not a tab, and while it is open the Bridges tab reads as the current one. One scrolling column, with Log inspection and Log issue pinned at the top and the Issue Inspector beneath them.',
      },
      {
        k: 'What is not there',
        v: 'The overview, the network summary and the inventory sheet are absent by design. They are network-wide documents for somebody with a printer, and nobody is printing in a gorge. So is People, which is register administration. Use the web app for all four.',
      },
    ],
    tasks: [
      {
        title: 'Install it on a phone or tablet',
        where: 'field',
        steps: [
          'Open the address in Chrome on Android, or Safari on iOS.',
          'Press Install, at the top right of the Bridges screen — it is there only where the browser has offered a prompt to capture.',
          'Options carries the same button, in the Install on this device card, along with what installing does and does not do.',
          'No button means the browser made no offer, and the card names its menu instead: Chrome and Edge carry Install app or Add to Home screen; Safari installs from Share, then Add to Home Screen.',
          'Open it from the home-screen icon. It runs full screen, with no browser bar.',
          'Open it once with a connection before going out, so the register and the records you need are cached on the device.',
        ],
        note:
          'Installed or not, the same application runs against the same register. What installing ' +
          'adds is the full screen, the home-screen icon, and a copy that opens with no signal.',
      },
    ],
  },

  {
    id: 'guide-roles',
    title: 'Roles: who may do what',
    lead:
      'Eight of them, and an account may hold several. The ones you hold decide which controls ' +
      'this application gives you.',
    paras: [
      'Two different systems decide what you can do here, and it is worth knowing which is ' +
        'which. Cloudflare Access decides whether you reach the application at all — that is the ' +
        'sign-in, and it is run from the Department’s Zero Trust settings rather than from here. ' +
        'Your role decides what you may do once you are in, and it is granted inside this ' +
        'application, on the People screen. Removing somebody from Access shuts the door; ' +
        'changing their role rearranges the room.',
      'A role is not a warning you get when you press something. It is enforced by the register ' +
        'itself: a control your role does not carry is not drawn, and a request that arrives ' +
        'without the permission is refused with the reason — from any screen, on any device, ' +
        'including one that was left open since before the role changed. That is deliberate. A ' +
        'permission system that only hid buttons would be a suggestion.',
      'Nobody is locked out by default. An account that has not been given a role reads the ' +
        'whole register — every structure, inspection, issue, photograph, model and printed ' +
        'document — and writes nothing to it. Saying no to somebody is suspending their account, ' +
        'which keeps their roles for when it is lifted and is not the same as removing it.',
      'Holding two roles never lets you accept your own work. If you computed a rating you cannot ' +
        'accept it, if you carried out an inspection you cannot accept it, and if you recorded a ' +
        'site entry you cannot review it — somebody else must, and the register refuses and says so.',
      'A role says nothing about WHERE. There is no such thing here as an engineer for one ' +
        'district only: the People screen records somebody’s division as a note about them, and ' +
        'it does not restrict which structures they may touch. A limit that read like one on ' +
        'screen and was not enforced would be worse than the plain truth, so it is not offered.',
    ],
    /* Read off ROLE_COPY rather than written out, which is the opposite of the
       rule this page follows for the screen list — and right for the opposite
       reason. The screen numbers are two statements of one fact and are checked
       against each other; these ARE the definitions the API enforces, and a
       second copy in prose would be a copy that could be wrong about what a role
       permits. What `npm test` checks here is that every role reaches this page. */
    facts: ROLES.map((role) => ({
      k: ROLE_COPY[role].label,
      v: ROLE_COPY[role].not
        ? `${ROLE_COPY[role].who} ${ROLE_COPY[role].not!}`
        : ROLE_COPY[role].who,
    })),
    tasks: [
      {
        title: 'Sign in, and get back in when the session ends',
        where: 'both',
        steps: [
          'Open the address and type your username and password.',
          'The first time, you will be using a password an administrator generated and read out to you. The register will not let you do anything else until you have replaced it with one only you know — at least twelve characters.',
          'If a session ends while you are working, every screen is replaced by the sign-in page. Sign in again and you come back to what you were doing.',
          'To sign out on purpose: Options → Profile → Sign out. On the web app, the corner with your name at the top right opens that screen.',
        ],
        note:
          'THERE IS NO PASSWORD RESET LINK, and there cannot be: this application sends no email ' +
          'and has no address to send one from. If you forget yours, a register administrator ' +
          'issues you a new one in a moment on the People screen. And do not reset offline ' +
          'storage to fix a sign-in problem — the sign-in page counts what is still waiting on ' +
          'the device, and a reset is the one thing that would lose it.',
      },
      {
        title: 'Find out what your own role is',
        where: 'both',
        steps: [
          'Web app: hover your name, top right, for your role and what it may do — or press it, and the account menu prints the role.',
          'Field app: Options, then the Profile card — it prints the role and what it permits, because there is nothing to hover on a phone.',
          'Check it against the list above. If it says No role, nobody has granted you one: you can read the whole register and write nothing to it.',
        ],
        note:
          'A control you were expecting and cannot find is nearly always this. The other ' +
          'possibility is the interface: a few things are on the web app only, and the headings ' +
          'above say which.',
      },
      {
        title: 'Add somebody to the register',
        where: 'web',
        steps: [
          'People, in the navigation pane. It is there only if you are an administrator.',
          'Press Grant a role. Type the username they will sign in with — an email address, or a plain name like r.thapa — tick their roles, and Save. This creates the account and decides what they may do.',
          'Find their row and press Issue password. The register generates one and shows it to you once.',
          'Read it to them. It is not stored and cannot be shown again — if you lose it, press Issue password again, which costs nothing.',
          'They sign in with it, and the register makes them choose their own before it lets them do anything else.',
        ],
        note:
          'TWO STEPS, AND THE SECOND IS THE ONE PEOPLE FORGET. A role with no password is an ' +
          'account nobody can get into; a password with no role reads the register and writes ' +
          'nothing. The username is what every inspection and issue they record is attributed ' +
          'to for ever, so it is worth agreeing on before you type it — changing it later means ' +
          'a new account, not a rename.',
      },
      {
        title: 'Give somebody a role',
        where: 'web',
        steps: [
          'People, in the navigation pane. It is there only if you are an administrator.',
          'Press Grant a role.',
          'Type the address Cloudflare Access verifies for them — the one they sign in with. For a machine, "service-token:" followed by its client id.',
          'Tick every role they hold. What they permit together is listed under the boxes as you tick them, so the decision is made against the permissions rather than against the words.',
          'Fill in their name, their division and why, then Save.',
        ],
        note:
          'It takes effect the next time their browser asks the register who they are, which is ' +
          'every time the application is opened. Somebody sitting on an open page keeps the ' +
          'controls they had until they reload — and the register refuses the writes anyway, so ' +
          'what they see is stale, not permitted.',
      },
      {
        title: 'Take somebody’s access away',
        where: 'web',
        steps: [
          'Decide which of the two you mean. Suspended means they may do nothing here at all, including read, and keeps their roles for when it is lifted. Removing their account takes the roles away with it.',
          'To suspend: People, Edit on their row, tick Suspended, Save. Every screen tells them the register is not open to them rather than showing them one.',
          'To remove the row: People, Remove on their row, and read what the confirmation says before agreeing to it.',
          'To stop them reaching the application at all, remove them from the Cloudflare Access policy. That is a Zero Trust setting and is not in this application.',
        ],
        note:
          'The register will not let you withdraw Administrator from, suspend or remove the last ' +
          'administrator: it would leave nobody able to grant the role back. Make somebody else an ' +
          'administrator first.',
      },
    ],
  },

  {
    id: 'guide-inspection',
    title: 'Recording an inspection',
    lead: 'A dated entry against a structure, rating its elements as you found them.',
    tasks: [
      {
        title: 'Log an inspection',
        where: 'both',
        steps: [
          'Open the record. Web app: Register, select the row, then Open record. Field app: Bridges, then tap it.',
          'Press Log inspection — in the Inspection ribbon on the web app, in the navigation pane while the register is open, or in the pinned bar at the top of the field record.',
          /* "how often DoR asks for it" implied the reader's own department set
             the interval. It does not: the 12- and 60-month cycles come from
             DoR's guideline and this build applies them to every register,
             which is a divergence a DPWH reader is entitled to see stated
             rather than hidden behind their own initials. */
          'Choose the type: Routine, Principal, Special or Underwater. The reference at the ' +
          'foot of the dialog says what each one is and the interval this register applies — ' +
          'which comes from the Nepal DoR guideline for every department, and is noted there.',
          'Set the date it was carried out — not today’s date, if you are writing it up afterwards.',
          'Rate the elements you assessed, on the scale your sheet uses: 0–4, 0–10 or 0–100. Leave alone anything you did not assess.',
          'Attach photographs if you have them. They are reduced on the device before they are stored, so a phone photograph does not cost a district connection several megabytes.',
          'Save.',
        ],
        note:
          'An inspection is a dated log entry: recorded once and kept as it was. There is no ' +
          'editing one afterwards — if it was wrong, the answer is a new entry. The register does ' +
          'not record who carried an inspection out, which is why the printed summary leaves ' +
          '"Inspected By" to be completed by hand.',
      },
    ],
  },

  {
    id: 'guide-defect',
    title: 'Logging an issue',
    lead: 'A finding against a component, with the measurement that says how bad it is.',
    tasks: [
      {
        title: 'Log an issue',
        where: 'both',
        steps: [
          'Open the record and press Log issue — beside the Issues panel on the web app, in the pinned bar on the field app.',
          /* Not DoR's, and never was: these nine codes are the modeller's own
             vocabulary, shared by the register so a finding logged in either
             counts once — see src/data/defects.ts. They are also, as it
             happens, the set the DPWH prototype used. */
          'Choose the issue code. This is a fixed vocabulary rather than free text, so the ' +
          'same fault is called the same thing on every structure, and a finding logged in ' +
          'the modeller and one logged here count together.',
          'Say where it is. The component list is read from the structure’s own 3D model, so it offers what is really there rather than inviting a name the model has never heard of.',
          'Set the severity.',
          'Measure it. Which boxes you get depends on the code: a crack asks for width and length, spalling asks for an area. Say how it was measured — ruler, tape, crack gauge, estimated, or from a photograph.',
          'Photograph it.',
          'Save.',
        ],
        note:
          'The component is optional on purpose: an issue somebody saw is worth recording even ' +
          'when they could not place it precisely, and forcing a location gets a wrong one. Where ' +
          'you give a crack width, the rating guide suggests a condition rating from it — a ' +
          'suggestion printed beside your number, never a rating set on your behalf.',
      },
      {
        title: 'Measure an issue on the photograph',
        where: 'both',
        steps: [
          'Attach a photograph in the Log issue dialog, then press Measure on the thumbnail.',
          'Set the scale first: mark something in the frame whose size you know — a scale bar, a coin, a marked rule — and type its real size. Nothing can be measured until this is done, because no photograph carries scale of its own.',
          'Trace the issue on the image.',
          'Read the result, and the arithmetic beside it.',
          'Accept it. It goes back into the issue you were logging, recorded as a measurement taken from a photograph.',
        ],
        note:
          'This assists; it does not detect. And it refuses: where the photograph cannot resolve ' +
          'what is being measured it declines to give a crack width and shows you why — though a ' +
          'two-metre length off that same photograph is fine, and is offered.',
      },
    ],
  },

  {
    id: 'guide-cost',
    title: 'Remedial work, and what it costs',
    lead: 'The register recommends the work. It prices it only from rates your division approved.',
    paras: [
      'Every open issue on a record produces a recommended remedial measure, and those ' +
        'recommendations are listed whether or not anything can be priced. What the register will ' +
        'not do is invent a rate. A district rate is approved per division per fiscal year, in an ' +
        'office this application has never seen; a plausible-looking figure produced here would ' +
        'come back in a budget submission quoted by somebody with no reason to doubt it.',
      'So it ships with no rates at all, and every line with nothing behind it prints the reason ' +
        'where the money would go. Load your division’s approved table and the same lines carry ' +
        'figures, each with the norm reference the rate was analysed from.',
    ],
    tasks: [
      {
        title: 'Load a division’s approved rates',
        where: 'web',
        steps: [
          'Sign in as the administrator and open a record in the district concerned, on the Bridge tab.',
          'Under Reference tables at the foot of the explorer, pick District rates. The dialog opens seeded with that structure’s district.',
          'Paste the rows from the division’s spreadsheet: one row per work item — treatment, unit, rate, norm reference — tab- or comma-separated, with or without a header.',
          'Read the faults. Every problem in the paste is listed at once rather than one at a time.',
          'Save. Every record in that district picks the table up.',
        ],
        note:
          'Rate tables are register administration and are deliberately absent from the field app: ' +
          'nobody types a rate table into a phone in the rain. A table approved long enough ago to ' +
          'be out of date says so rather than being used quietly.',
      },
    ],
  },

  {
    id: 'guide-print',
    title: 'The printed documents',
    lead: 'Three of them, and each has a job in a process outside this application.',
    facts: [
      {
        k: 'Bridge Inspection Summary',
        /* The citation stays whole. This IS a Nepal DoR form and every register
           in this application uses it; saying so is the documented divergence a
           second department gets instead of a silent substitution. What changed
           is "DoR's own form", which read as the reader's own. */
        v:
          'The Nepal DoR condition form — Guidelines for Inspection and Maintenance of ' +
          'Bridges, Volume 1 Part 2, Appendix C — filled in from what the register holds. ' +
          'Every department here uses it, because the condition, extent and urgency scales ' +
          'are the same for all of them. Ten items with Condition, Extent, Urgency and ' +
          'Comments, then the recommendations and the signature block. Opened from the ' +
          'Recommended remedial work panel on the record.',
      },
      {
        k: 'Inventory sheet',
        v: 'One structure’s inventory, as a sheet. Screen 5, or Inventory sheet in the Inspection ribbon.',
      },
      {
        k: 'Construction report',
        v: 'What was built, over a period. Screen 7.',
      },
      {
        k: 'Network summary',
        v: 'The register as a report, grouped by district, division road office or road class. Network summary on the Home page.',
      },
    ],
    paras: [
      'All three print what the register holds and leave the rest blank. An item nobody assessed ' +
        'prints empty, exactly as it would on paper; a recommendation nobody can price prints the ' +
        'reason. A printed sheet outlives the screen it came from and is read by people who cannot ' +
        'ask it questions, so a gap is shown as a gap rather than filled with a zero.',
    ],
  },

  {
    id: 'guide-models',
    title: '3D models and the Issue Inspector',
    lead: 'Where the geometry comes from, and the page that lets you put an issue on it.',
    tasks: [
      {
        title: 'Import a 3D model onto a record',
        where: 'web',
        steps: [
          'Open the structure’s dashboard on the start page and press Import 3D model in its 3D model tile.',
          'Choose the CSiBridge export — the Components bundle .json, and the alignment file with it if you have one.',
          'Leave it running. The import continues in the background while you carry on, and reports its progress on the button.',
          'It saves to the cloud rather than to this browser, so every device that opens the record afterwards sees the model.',
        ],
      },
      {
        title: 'Place issues on the model',
        where: 'both',
        steps: [
          'Press Issue Inspector beneath the pinned buttons on the field record.',
          'It opens as its own page, on the structure it names. The address is inspector.html?bridge= followed by the bridge id, which makes it a link you can send to somebody.',
          'Frame what you want with the toolbar: zoom in, zoom out, zoom all, the six standard views, the projection switch, and the orbit point.',
          'Tap the model where the issue is. The tap records the finding at that point on the geometry.',
          'Fill it in and save. It becomes an ordinary issue on the record, in the same log as one entered through Log issue.',
        ],
        note:
          'The Inspector opens on a structure with no model at all — it says which nothing it is ' +
          'looking at, and its component list still works. For a model this device has already ' +
          'fetched it works with no signal, because the geometry and the photographs come out of ' +
          'the same cache the rest of the application uses.',
      },
    ],
  },

  {
    id: 'guide-data',
    title: 'Getting data in',
    lead: 'Rows from a spreadsheet, and a structure’s position on the ground.',
    tasks: [
      {
        title: 'Import inventory rows from a spreadsheet',
        where: 'web',
        steps: [
          'Register, then Import.',
          'Paste the rows.',
          'Read the plan. It says what would be created, what would be changed, and what it could not read — before anything is written.',
          'Apply it, or close the dialog and fix the spreadsheet. Nothing is written until you approve the plan.',
        ],
      },
      {
        title: 'Put a structure in the right place on the map',
        where: 'web',
        steps: [
          'Network, in the Flat view, with the structure selected.',
          'Zoom in until the crossing is on screen. Below zoom 14 the control is disabled and says why — a pixel is hundreds of metres there, and there is nothing to line the deck up against.',
          'Press Adjust position. Two handles appear on the selected structure.',
          'Drag the pin to move it; drag the grip on the end of the deck to turn it. The bar along the bottom reads out the coordinate, the axis, and how far it has moved.',
          'Press Save position. Cancel drops the whole thing and leaves the record as it was.',
        ],
        note:
          'Nothing touches the register until Save position, and a save that fails keeps your work ' +
          'on screen with the reason beside it. What is not recorded is who moved a structure or ' +
          'why — positions have no audit trail yet.',
      },
    ],
  },

  {
    id: 'guide-offline',
    title: 'Working without a connection',
    lead: 'What still works in a gorge, what does not, and where your entries are in the meantime.',
    facts: [
      {
        k: 'What works',
        v: 'The register, every bridge record, logging an inspection, logging an issue, photographs, and the Issue Inspector for a model this device has already fetched.',
      },
      {
        k: 'What does not',
        /* What the fallback IS depends on the department: Nepal bundles a
           national outline, and a department without one gets the extent and
           the structures. Saying "an outline of Nepal" to a DPWH reader
           describes a layer their map does not have. */
        v:
          'The map. Tiles come from a tile server, and no tile server is reachable with no ' +
          'signal. ' +
          (active().geo.outline
            ? `The Flat view falls back to a bundled outline of ${active().countryName} with ` +
              'the structures still in their right places, and says which of the two you are ' +
              'looking at.'
            : 'The Flat view then draws the extent and the structures without a base map, and ' +
              'says which of the two you are looking at. No national outline is bundled for ' +
              'this register.'),
      },
      {
        k: 'Where your entries go',
        v: 'Onto this device, queued. They appear on the record straight away, and the pill in the web app’s title bar and the Data & sync card in the field app’s Options both count them.',
      },
      {
        k: 'When they are sent',
        v: 'As soon as there is a connection, or when you press the send button on the Options screen — it counts what is waiting, so it reads "Send 3 now". Closing the app does not send them, and nothing is lost if an attempt fails.',
      },
    ],
    paras: [
      'Before going out, open the application once with a connection: that is what fills the cache ' +
        'the offline session reads from. Before resetting anything, check the Options screen — it ' +
        'says how many entries have not reached the register yet, and a reset cannot bring them back.',
    ],
  },

  {
    id: 'guide-honest',
    title: 'Reading what it tells you',
    lead: 'The rules this register follows about what it will and will not claim. Worth knowing before you quote a number from it.',
    facts: [
      {
        k: 'Empty is not zero',
        v: 'A field the as-built drawing does not carry is left empty. Nothing is zeroed to make a form look complete.',
      },
      {
        k: 'A dash is not a nil return',
        v: '"None logged" and "we could not ask" are different answers, and the screens distinguish them. Where the register could not be reached, the figure is a dash and says why.',
      },
      {
        k: '"Not screened" is not "fine"',
        v: 'It means the register does not hold what the flood, scour and seismic screen needs. The overview lists what would unblock those structures, most useful first.',
      },
      {
        k: 'No rates means no figure',
        v: 'Recommended work with no approved rate behind it is still listed, with the reason printed where the money would go.',
      },
      {
        k: 'Geometry is transcribed',
        v: 'From as-built general-arrangement drawings, not from survey. A 3D model is a reconstruction of what the drawing says.',
      },
      {
        k: 'Map positions are pins',
        v: 'Not surveyed points — chainage is the location of record. The topographic contours are SRTM at roughly 30 m sampling: they read the shape of a valley and have nothing to say about a deck level recorded to the centimetre.',
      },
    ],
  },

  {
    id: 'guide-keys',
    title: 'Keyboard and gestures',
    lead: 'What the web app answers to, and how the 3D views are handled.',
    facts: [
      {
        /* The widest range any application offers, read off their own lists
           rather than typed — a screen added to one of them cannot leave this
           line advertising a range that stops short. */
        k: `1 – ${Math.max(...APPS.map((a) => a.screens.length))}`,
        v: 'Jump to a screen of the application you are in, in navigation-pane order. The key is printed on each item in the pane, and the same digit reaches a different screen in a different application.',
      },
      { k: 'b', v: 'Collapse or expand the navigation pane.' },
      { k: 'Esc', v: 'Close whichever dialog is open.' },
      { k: 'Enter', v: 'On a register row, select it. Opening the record is the explicit button.' },
      {
        k: 'While a dialog is open',
        v: 'The number keys are suspended, so a stray 2 cannot throw the register up behind what you are typing into.',
      },
      {
        k: 'The 3D views',
        v: 'Drag to orbit, right-drag to pan, wheel or pinch to zoom. In the Issue Inspector every one of those is also a button on the toolbar, because a gesture nobody performs is a capability nobody has.',
      },
    ],
  },

  {
    id: 'guide-trouble',
    title: 'When something goes wrong',
    lead: 'The nine things that actually go wrong, and the fix for each.',
    facts: [
      {
        k: 'It will not load, or shows out-of-date information',
        v: 'Options → Help & support → Reset offline storage. It clears what this device has cached and reloads from the register. Send anything waiting to sync first — a reset cannot bring it back. If the screen will not open at all, adding ?sw=off to the address does the same thing.',
      },
      {
        k: 'I have forgotten my password',
        v: 'Ask a register administrator. On the People screen they press Issue password against your name, and the register generates one and shows it to them once — they read it to you, you sign in with it, and it makes you choose your own straight away. There is no reset email from this application, so there is nothing to wait for and nothing in a spam folder.',
      },
      {
        k: 'It says the account is locked',
        v: 'Five wrong passwords in a row locks an account for five minutes, and longer if it keeps happening. It unlocks by itself — wait, and try again with the right one. An administrator can also issue a new password, which clears it immediately.',
      },
      {
        k: 'It says my session has ended',
        v: 'Press Sign in again on that screen. It happens because an Access session has a lifetime and yours reached it, or because the app was opened from its offline copy with no session behind it. Nothing recorded on the device is lost — that screen counts what is still waiting and sends it once you are back. Do not reset offline storage to fix this.',
      },
      {
        k: 'A button I used to have is gone',
        v: 'Almost always your role. Hover your name in the top right of the web app, or the Profile card on the field app’s Options screen, and check it against the roles above. A register administrator can change it on the People screen. The other possibility is the interface — a few controls are on the web app only.',
      },
      {
        k: 'The wrong interface opened',
        v: 'Options → This device → Interface switches it, and the choice is remembered. Or open the address with ?shell=field or ?shell=desk.',
      },
      {
        k: 'The map is blank',
        v: 'Either there is no connection — in which case the Flat view still draws the structures on the bundled outline — or the tile host is down. Street and Topo are different servers, so try the other one.',
      },
      {
        k: 'My entries have not reached anybody else',
        v: 'Options → Data & sync says how many are still on this device and why the last attempt failed. Press the send button there with a connection.',
      },
      {
        k: 'A link opened the wrong structure',
        v: 'A link naming a structure that is not on file falls back to the first record rather than showing an empty screen. Check the id in the address.',
      },
    ],
  },
];

/* --------------------------- rendering --------------------------- */

/**
 * Scroll a section into view.
 *
 * Optional-called because jsdom does not implement scrollIntoView, and a
 * contents list that threw under the test runner would make this screen
 * unrenderable in CI for the sake of a convenience.
 */
function goToSection(id: string): void {
  const el = document.getElementById(id);
  el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function taskBlock(task: Task): HTMLElement {
  return h(
    'div',
    { class: 'guide__task' },
    h(
      'div',
      { class: 'guide__taskhead' },
      h('h3', { class: 'guide__tasktitle', text: task.title }),
      tag(WHERE_LABEL[task.where], WHERE_RANK[task.where]),
    ),
    h(
      'ol',
      { class: 'guide__steps' },
      ...task.steps.map((step) => h('li', { class: 'guide__step', text: step })),
    ),
    task.note ? h('p', { class: 'guide__note', text: task.note }) : null,
  );
}

/* `at` prefixes the anchor ids, so the docked pane and the guide screen can both
   be up without one's contents scrolling the other. */
function sectionBlock(section: Section, at = ''): HTMLElement {
  return card(
    { pad: true, class: 'guide__sec' },
    /* The anchor is an empty element at the top of the card rather than an id on
       the heading, so scrolling to a section puts the card's top edge at the top
       of the scroller instead of clipping its padding above the heading. */
    h('div', { id: at + section.id, class: 'guide__anchor' }),
    h('h2', { class: 'guide__h', text: section.title }),
    h('p', { class: 'guide__lead', text: section.lead }),
    ...(section.paras ?? []).map((p) => h('p', { class: 'guide__p', text: p })),
    section.facts
      ? h('div', { class: 'guide__facts' }, ...section.facts.map((f) => fact(f.k, f.v)))
      : null,
    ...(section.tasks ?? []).map(taskBlock),
  );
}

/**
 * The contents.
 *
 * Buttons rather than anchors, deliberately: this application routes on the
 * fragment (see src/core/router.ts), so `href="#guide-defect"` would be read by
 * the router as an address, fail to decode as one, and land the reader on the
 * overview. A button that scrolls has no such argument with the address bar.
 */
function contents(at = ''): HTMLElement {
  return card(
    { pad: true },
    cardHead('Contents', `${SECTIONS.length} sections`),
    h(
      'div',
      { class: 'guide__toc' },
      ...SECTIONS.map((section) =>
        h('button', {
          type: 'button',
          class: 'chip guide__tocitem',
          text: section.title,
          onclick: () => goToSection(at + section.id),
        }),
      ),
    ),
  );
}

/** The opening panel: what this is, and the two ways into it. */
function intro(s: Readonly<State>): HTMLElement {
  return card(
    { pad: true, class: 'guide__intro' },
    h(
      'div',
      { class: 'guide__introhead' },
      h('span', { class: 'guide__introicon' }, icon(ICONS.book, { size: 20 })),
      h(
        'div',
        { style: 'min-width:0' },
        h('h2', { class: 'guide__h', style: 'margin:0', text: 'How to use Bridge Watch' }),
        h('p', {
          class: 'guide__lead',
          style: 'margin:4px 0 0',
          text: `${agency().department} · ${agency().branch}`,
        }),
      ),
    ),
    h('p', {
      class: 'guide__p',
      text:
        'This is the whole application explained in the order somebody meets it: what it is for, ' +
        'what is on each screen of the web app and each tab of the field app, and then the steps ' +
        'for every job it exists to do — an inspection, an issue, a measurement, an estimate, a ' +
        'printed sheet, an import. The last three sections are the ones worth reading before ' +
        'quoting anything from this register to anybody.',
    }),
    h(
      'div',
      { class: 'guide__introrow' },
      /* Straight to the two sections a first-time reader is nearly always after,
         so the guide does not open on a wall of contents. */
      button('The applications', {
        primary: true,
        iconPath: ICONS.grid,
        title: `The ${DESK_APPS.length} applications of the desktop interface`,
        onClick: () => goToSection('guide-web'),
      }),
      button('The field app', {
        iconPath: ICONS.list,
        title: `The ${FIELD_TABS.length} tabs of the phone and tablet interface`,
        onClick: () => goToSection('guide-field'),
      }),
      /* The home page is another document — this guide is carried by all four
         applications, and only one of them draws the home page — so this leaves
         for it rather than calling `actions.go`, which is in-app navigation and
         would have set a screen the pane cannot show. See src/core/navigate.ts.

         Not on the field shell, which refuses the home page outright: a button
         that navigated to the register instead would be a button whose label was
         a lie. See FIELD_SCREENS in src/core/shell.ts. */
      s.shell === 'field'
        ? null
        : button('Back to the home page', {
            iconPath: ICONS.grid,
            title: 'The register, summarised, and the way in to each application',
            onClick: () => openApp('home'),
          }),
    ),
  );
}

export function guideScreen(s: Readonly<State>): HTMLElement {
  return h(
    'div',
    { class: 'guide' },
    intro(s),
    contents(),
    ...SECTIONS.map((section) => sectionBlock(section)),
    h('p', {
      class: 'guide__foot',
      text:
        'This guide is part of the application and is available with no connection, like the ' +
        'records themselves. Where it names a control, that is the word on the control — if the ' +
        'two ever disagree, the application is right and this page is out of date.',
    }),
  );
}

/**
 * The guide docked on the right of the web app, beside whatever screen is up and
 * in every application, until it is closed. The same sections as the screen,
 * without the opening panel: its buttons are ways into this page, and the pane is
 * already open beside the thing they would lead to.
 */
export function guidePane(): HTMLElement {
  return panel(
    {
      title: 'Guide',
      class: 'guide-pane',
      actions: [
        panelButton({
          icon: ICONS.x,
          title: 'Close the guide',
          label: 'Close the guide',
          onclick: () => actions.closeGuide(),
        }),
      ],
    },
    h(
      'div',
      { class: 'guide guide--pane' },
      contents('pane-'),
      ...SECTIONS.map((section) => sectionBlock(section, 'pane-')),
    ),
  );
}

/** The sections, for the checks that verify what this page claims. */
export const GUIDE_SECTIONS: readonly Section[] = SECTIONS;

/** Every task on the page, flattened — same reason. */
export const GUIDE_TASKS: readonly Task[] = SECTIONS.flatMap((s) => s.tasks ?? []);

export type { Section as GuideSection, Task as GuideTask, Where as GuideWhere };
